import type { ComponentType, ErrorInfo } from 'react';
import React, { Component } from 'react';
import type { Hub } from '@sentry/types';

declare global {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Window {
        Sentry: Hub;
    }
}

interface IDefaultProps {
    fallbackProps?: Record<string, unknown> | null;
    showFallback?: boolean;
}

interface IState {
    hasErrors: boolean;
}

/**
 * Функция hoc, для отлова ошибки в компоненте
 * Принимает FallbackCmp, logger
 * Рендерит Fallback, если в компоненте произошла ошибка
 * Пишет ошибку в logger
 *
 * TODO: Подумать над механизмом ререндеринга компонента, после обновления, если изменились пропсы и компонент не упал
 */
export const withErrorBoundary = <T,>(
    Widget: ComponentType<React.PropsWithChildren<T>>,
    FallbackCmp?: ComponentType<React.PropsWithChildren<unknown>>
) => {
    return class WithErrorBoundary extends Component<
        React.PropsWithChildren<T & IDefaultProps>,
        IState
    > {
        static displayName = Widget.displayName;

        static defaultProps = {
            fallbackProps: null,
            showFallback: false,
        } as T & IDefaultProps;

        constructor(props: T & IDefaultProps) {
            super(props);

            this.state = {
                hasErrors: false,
            };
        }

        static logComponentError(component: Component, error: Error, errorInfo: ErrorInfo) {
            if (!window || !window.Sentry) {
                return;
            }

            window.Sentry.withScope(scope => {
                Object.entries(errorInfo).forEach(([key, value]) => {
                    scope.setExtra(key, value);
                });
                scope.setExtra('props', component.props);
                scope.setExtra('state', component.state);
                scope.setTag('logger', 'hoc.withErrorBoundary');
                window.Sentry.captureException(error);
            });
        }

        static getDerivedStateFromError() {
            return { hasErrors: true };
        }

        componentDidCatch(error: Error, errorInfo: ErrorInfo) {
            console.error(
                `Widget ${WithErrorBoundary.displayName} has not been rendered because of an error occurred within the widget`,
                'Error ---->',
                error
            );
            WithErrorBoundary.logComponentError(this, error, errorInfo);
        }

        render() {
            const { fallbackProps, showFallback, ...widgetProps } = this.props;
            const { hasErrors } = this.state;

            // Принудительно перевести в режим заглушки. См. описание примера в сториз.
            if (showFallback && FallbackCmp) {
                return <FallbackCmp {...fallbackProps} />;
            }

            if (!hasErrors) {
                return <Widget {...(widgetProps as React.PropsWithChildren<T>)} />;
            }

            if (FallbackCmp) {
                return <FallbackCmp {...fallbackProps} />;
            }

            return null;
        }
    };
};
